// frontend/src/App.tsx
import { useLocation, Navigate } from 'react-router-dom';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext';
import { PwaNavProvider, usePwaNav } from './contexts/PwaNavContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import AnimatedOverlay from './components/AnimatedOverlay';
import HomePage from './pages/HomePage';
import OrderStatus from './pages/OrderStatus';
import OrderHistory from './pages/OrderHistory';
import LoginClientPage from './pages/LoginClientPage';
import PassportPage from './pages/PassportPage';
import GiftPage from './pages/GiftPage';
import FriendProfileModal from './components/FriendProfileModal';
import AdminPanel from './pages/admin/AdminPanel';
import CustomerDisplay from './pages/CustomerDisplay';
import KitchenDisplay from './pages/KitchenDisplay';
import Login from './pages/Login';
import JoinPage from './pages/JoinPage';
import LegalPage from './pages/LegalPage';
import SimphonyDemoPage from './pages/SimphonyDemoPage';
import LegalFooter from './components/LegalFooter';
import UpdateToast from './components/UpdateToast';
import SettingsPage from './pages/SettingsPage';
import VenueLandingPage from './pages/VenueLandingPage';
import RestaurantMenuPage from './pages/RestaurantMenuPage';
import FeedPage from './pages/FeedPage';
import FriendsPage from './pages/FriendsPage';
import BottomNav from './components/BottomNav';
import { useSyncFcmToken } from './hooks/useSyncFcmToken';
import './App.css';

function StaffRoutes() {
  const { pathname } = useLocation();
  const isLegal = pathname.startsWith('/legal');
  return (
    <>
      <Routes>
        <Route path="/display" element={<CustomerDisplay />} />
        <Route path="/login" element={<Login />} />
        <Route path="/join" element={<JoinPage />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminPanel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/kitchen"
          element={
            <ProtectedRoute>
              <KitchenDisplay />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      {!isLegal && <LegalFooter />}
    </>
  );
}

function CustomerShellInner() {
  const { showOrder, showPassport, showHistory, showLogin, showGift, showFriendProfile, showSettings, showFeed, showRestaurant, showVenue, showFriends } = usePwaNav();
  useSyncFcmToken();

  return (
    <div className="app">
      <UpdateToast />
      <Routes>
        <Route path="*" element={<HomePage />} />
      </Routes>
      <AnimatedOverlay show={showFeed}>
        <FeedPage />
      </AnimatedOverlay>
      <AnimatedOverlay show={showRestaurant}>
        <Routes>
          <Route path="/r/:restaurantSlug" element={<RestaurantMenuPage />} />
          <Route path="*" element={<RestaurantMenuPage />} />
        </Routes>
      </AnimatedOverlay>
      <AnimatedOverlay show={showVenue}>
        <Routes>
          <Route path="/v/:venueSlug" element={<VenueLandingPage />} />
          <Route path="*" element={<VenueLandingPage />} />
        </Routes>
      </AnimatedOverlay>
      <AnimatedOverlay show={showOrder}>
        {/* Routes wrapper needed so useParams() in OrderStatus receives qrCode */}
        <Routes>
          <Route path="/order/:qrCode/ct/:ct" element={<OrderStatus />} />
          <Route path="/order/:qrCode" element={<OrderStatus />} />
          {/* Fallback route for PWA standalone mode where URL doesn't change */}
          <Route path="*" element={<OrderStatus />} />
        </Routes>
      </AnimatedOverlay>
      <AnimatedOverlay show={showPassport}>
        <PassportPage />
      </AnimatedOverlay>
      <AnimatedOverlay show={showHistory}>
        <OrderHistory />
      </AnimatedOverlay>
      <AnimatedOverlay show={showLogin}>
        <LoginClientPage />
      </AnimatedOverlay>
      <AnimatedOverlay show={showGift}>
        <Routes>
          <Route path="/gift" element={<GiftPage />} />
          <Route path="*" element={<GiftPage />} />
        </Routes>
      </AnimatedOverlay>
      <AnimatedOverlay show={showFriendProfile}>
        <Routes>
          <Route path="/friend-profile" element={<FriendProfileModal />} />
          <Route path="*" element={<FriendProfileModal />} />
        </Routes>
      </AnimatedOverlay>
      <AnimatedOverlay show={showSettings}>
        <SettingsPage />
      </AnimatedOverlay>
      <AnimatedOverlay show={showFriends}>
        <FriendsPage />
      </AnimatedOverlay>
      <BottomNav />
    </div>
  );
}

function CustomerShell() {
  return (
    <CustomerAuthProvider>
      <PwaNavProvider>
        <CustomerShellInner />
      </PwaNavProvider>
    </CustomerAuthProvider>
  );
}

function App() {
  const { pathname } = useLocation();
  const isLegalRoute = /^\/legal(\/|$|\?)/.test(pathname);
  const isStaffRoute = /^\/(admin|kitchen|login|display|join)(\/|$)/.test(pathname);
  const isPublicLandingRoute = /^\/(v|r)(\/|$)/.test(pathname);
  const isSimphonyDemoRoute = /^\/simphony-demo(\/|$)/.test(pathname);

  if (isLegalRoute) {
    return (
      <ErrorBoundary>
        <Routes>
          <Route path="/legal" element={<LegalPage />} />
        </Routes>
      </ErrorBoundary>
    );
  }

  if (isSimphonyDemoRoute) {
    return (
      <ErrorBoundary>
        <Routes>
          <Route path="/simphony-demo" element={<SimphonyDemoPage />} />
        </Routes>
      </ErrorBoundary>
    );
  }

  if (isPublicLandingRoute) {
    return (
      <ErrorBoundary>
        <CustomerAuthProvider>
          {/* Both landing pages call usePwaNav(), so they need the provider
              even outside the PWA shell — without it they throw on mount. */}
          <PwaNavProvider>
            <Routes>
              <Route path="/v/:venueSlug" element={<VenueLandingPage />} />
              <Route path="/r/:restaurantSlug" element={<RestaurantMenuPage />} />
            </Routes>
          </PwaNavProvider>
        </CustomerAuthProvider>
      </ErrorBoundary>
    );
  }

  if (isStaffRoute) {
    return (
      <ErrorBoundary>
        <AuthProvider>
          <StaffRoutes />
        </AuthProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
        <CustomerShell />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
